/* eslint-disable */
import { useState, useEffect, useMemo, lazy, Suspense } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Container,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme
} from '@mui/material'
import HomeIcon from '@mui/icons-material/Home'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { propertyAPI, masterDataAPI, propertyDraftAPI } from '../../services/api'
import { PropertyCard } from '../../Components/PropertyCard'
import { PropertyListView } from '../../Components/PropertyListView'
import { ViewModeSelector } from '../../Components/ViewModeSelector'
import { useAuth } from '../../Contexts/AuthContext'
import { useToast } from '../../context/ToastContext'
import { transformProperties } from '../../Utils/propertyTransform'
import { encryptRouteParam } from '../../Utils/encrypt-decrypt'
import MainContentWrapper from '../../Layouts/MainContent/mainContent'
import './myProperties.scss'

// Lazy load PropertyListingForm and PropertyMapView
const PropertyListingForm = lazy(() => import('../../Components/PropertyListingForm'))
const PropertyMapView = lazy(() => import('../../Components/PropertyMapView'))

const LISTING_STATUS_OPTIONS = [
  { id: 1, value: 'Listed', label: 'Listed' },
  { id: 2, value: 'Unlisted/Inactive', label: 'Unlisted/Inactive' },
  { id: 3, value: 'Pending Verification', label: 'Pending Verification' },
  { id: 4, value: 'Archived', label: 'Archived' },
  { id: 5, value: 'Draft', label: 'Draft' },
]

export function MyProperties() {
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobileView = useMediaQuery(theme.breakpoints.down('md'))
  const { user, isLoggedIn, logout } = useAuth()
  const toast = useToast()
  const [properties, setProperties] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [isListingFormOpen, setIsListingFormOpen] = useState(false)
  const [editingProperty, setEditingProperty] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [statusOptions, setStatusOptions] = useState(LISTING_STATUS_OPTIONS)
  const [viewMode, setViewMode] = useState('grid')
  const [counts, setCounts] = useState({ total: 0, listed: 0, draft: 0 })
  const [individualStatusCounts, setIndividualStatusCounts] = useState([])
  const [statusPageStart, setStatusPageStart] = useState(0)
  const [propertyTypes, setPropertyTypes] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    hasMore: false,
  })
  const statusPageSize = useMemo(() => {
    if (isMobileView) return 1
    return 2
  }, [isMobileView])

  useEffect(() => {
    setStatusPageStart(0)
  }, [statusPageSize])

  useEffect(() => {
    fetchMyProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
  }, [statusFilter, typeFilter, debouncedSearchTerm])

  useEffect(() => {
    const shouldOpenListModal =
      Boolean(location.state?.openListProperty) ||
      location.state?.intent === 'list-property'

    if (!shouldOpenListModal) return

    setEditingProperty(null)
    setIsListingFormOpen(true)

    const nextState = { ...(location.state || {}) }
    delete nextState.openListProperty
    delete nextState.intent

    navigate('/my-properties', {
      replace: true,
      state: Object.keys(nextState).length ? nextState : undefined,
    })
  }, [location.state, navigate])

  const handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return
    setDebouncedSearchTerm(searchTerm.trim())
  }

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const response = await masterDataAPI.getAllMasterData()
        const data = response.data.data
        setPropertyTypes(data.propertyTypes || [])
      } catch (error) {
        console.error('Error fetching master data:', error)
      }
    }
    fetchMasterData()
  }, [])

  useEffect(() => {
    const fetchStatuses = async () => {
      try {
        const response = await masterDataAPI.fetchStatus()
        const payload = response?.data?.data
        const statuses = Array.isArray(payload)
          ? payload
          : (payload?.statuses || payload?.statusList || response?.data?.statuses || response?.data?.data || [])

        if (Array.isArray(statuses)) {
          const normalizedStatuses = statuses
            .map((item, index) => {
              if (typeof item === 'string' || typeof item === 'number') {
                return {
                  id: index + 1,
                  value: String(item),
                  label: String(item),
                }
              }

              if (item && typeof item === 'object') {
                let rawValue = item.status ?? item.value ?? item.name ?? item.label

                if (rawValue && typeof rawValue === 'object') {
                  rawValue =
                    rawValue.status ??
                    rawValue.value ??
                    rawValue.name ??
                    rawValue.label ??
                    Object.values(rawValue).find((value) => typeof value === 'string' || typeof value === 'number')
                }

                if (typeof rawValue === 'string' || typeof rawValue === 'number') {
                  const primitive = String(rawValue)
                  return {
                    id: item.id ?? index + 1,
                    value: primitive,
                    label: primitive,
                  }
                }
              }

              return null
            })
            .filter(Boolean)

          if (normalizedStatuses.length > 0) {
            const draftOptions = normalizedStatuses.filter(
              (item) => String(item?.value || item?.label || '').trim().toLowerCase() === 'draft',
            )
            const nonDraftOptions = normalizedStatuses.filter(
              (item) => String(item?.value || item?.label || '').trim().toLowerCase() !== 'draft',
            )
            setStatusOptions([...nonDraftOptions, ...draftOptions])
          }
        }
      } catch (error) {
        console.error('Error fetching statuses:', error)
      }
    }

    fetchStatuses()
  }, [])

  const fetchMyProperties = async (
    page = 1,
    append = false,
    selectedStatus = statusFilter,
    selectedType = typeFilter,
    selectedSearch = debouncedSearchTerm,
  ) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }

      const filters = {
        page,
        limit: 12,
        sortBy: 'newest',
        status: selectedStatus || 'all',
      }

      if (selectedType) {
        filters.propertyType = selectedType
      }

      if (selectedSearch) {
        filters.q = selectedSearch
      }

      const response = await propertyAPI.getMyProperties(filters)

      const fetchedProperties = response?.data?.data?.properties || response?.data?.properties || response?.data?.data || []

      // Transform properties to ensure consistent data structure and image handling
      const transformedProperties = transformProperties(fetchedProperties)

      if (append) {
        setProperties((prev) => [...prev, ...transformedProperties])
      } else {
        setProperties(transformedProperties)
      }

      // Get counts from API response
      const total = response?.data?.total || response?.data?.count || transformedProperties.length
      const listed = response?.data?.listedCount || 0
      const draft = response?.data?.draftCount || 0
      
      setCounts({ total, listed, draft })
      setIndividualStatusCounts(Array.isArray(response?.data?.individualStatusCounts) ? response.data.individualStatusCounts : [])
      setStatusPageStart(0)

      setPagination({
        page: response?.data?.page || page,
        limit: response?.data?.limit || 12,
        total,
        hasMore: Boolean(response?.data?.hasMore),
      })
    } catch (err) {
      toast.showError(err.response?.data?.message || 'Failed to load your properties')
    } finally {
      if (append) {
        setLoadingMore(false)
      } else {
        setLoading(false)
      }
    }
  }

  const handleViewDetails = async (property) => {
    const isDraft = property.isDraft === true || property.status?.toLowerCase() === 'draft' || property.status?.toLowerCase() === 'pending';
    const draftId = property.draft_id || property.draftId;
    
    if (isDraft && draftId) {
      // For draft properties, navigate to property details page with draft ID
      window.open(`/property/draft/${draftId}`, '_blank', 'noopener,noreferrer');
    } else {
      // For listed properties, navigate with encrypted actual property_id
      const actualPropertyId = property.property_id || property.propertyId || property.propertyCode || property.id;
      const encryptedPropertyId = encryptRouteParam(actualPropertyId);
      window.open(`/property/${encryptedPropertyId}`, '_blank', 'noopener,noreferrer');
    }
  }

  const handleListPropertyClick = () => {
    setEditingProperty(null) // Clear editing property for new listing
    setIsListingFormOpen(true)
  }

  const handleSignIn = () => {
    navigate('/login')
  }

  const handleSignOut = () => {
    logout()
    navigate('/')
  }

  const handleHomeClick = () => {
    navigate('/')
  }

  const handleMyPropertiesClick = () => {
    navigate('/my-properties')
  }

  const handleManageMyPropertiesClick = () => {
    navigate('/property-list')
  }

  const handleSavedPropertiesClick = () => {
    navigate('/saved-properties')
  }

  const handleMyProfileClick = () => {
    navigate('/my-profile')
  }

  const handleEditProperty = (property) => {
    setEditingProperty(property)
    setIsListingFormOpen(true)
  }

  const handleDeleteProperty = async (property) => {
    const isDraft = property.isDraft === true || property.status?.toLowerCase() === 'draft' || property.status?.toLowerCase() === 'pending';
    const propertyId = property.property_id || property.id;
    const draftId = property.draft_id || property.draftId;
    
    if (window.confirm('Are you sure you want to delete this property?')) {
      try {
        if (isDraft && draftId) {
          // Use draft delete API for draft properties
          await propertyDraftAPI.deleteDraftById(draftId);
        } else {
          // Use regular property delete API for listed properties
          await propertyAPI.deleteProperty(propertyId);
        }
        toast.showSuccess('Property deleted successfully')

        // Invalidate the main properties cache so home page shows updated data
        sessionStorage.removeItem('propertiesCache')

        fetchMyProperties(1, false, statusFilter) // Refresh the list
      } catch (err) {
        toast.showError(err.response?.data?.message || 'Failed to delete property')
      }
    }
  }

  const handleUnlistProperty = async (property) => {
    const propertyId = property.property_id || property.propertyId || property.propertyCode || property.id

    if (!propertyId) {
      toast.showError('Unable to identify property for unlisting')
      return
    }

    try {
      let existingPropertyData = property || {}
      const hasRequiredType = Boolean(existingPropertyData?.property_type || existingPropertyData?.propertyType)

      if (!hasRequiredType) {
        const propertyDetailResponse = await propertyAPI.getPropertyById(propertyId)
        existingPropertyData =
          propertyDetailResponse?.data?.data?.property ||
          propertyDetailResponse?.data?.property ||
          propertyDetailResponse?.data?.data ||
          propertyDetailResponse?.data ||
          {}
      }

      const {
        lead_by,
        flashcard_keyword,
        search_keywords,
        estimated_current_value,
        ...existingPropertyPayload
      } = existingPropertyData

      const unlistPayload = {
        ...existingPropertyPayload,
        property_id: propertyId,
        propertyId: propertyId,
        currentAvailability: 'Unavailable',
        current_availability: 'Unavailable',
      }

      await propertyAPI.updateProperty(propertyId, unlistPayload)
      toast.showSuccess('Property unlisted successfully')

      // Invalidate cache so other listing surfaces pick up latest availability.
      sessionStorage.removeItem('propertiesCache')
      fetchMyProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
    } catch (err) {
      toast.showError(err.response?.data?.message || 'Failed to unlist property')
    }
  }

  const handleCloseListingForm = () => {
    setIsListingFormOpen(false)
    setEditingProperty(null)

    // Ensure My Listing is refreshed whenever the listing modal closes
    fetchMyProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
  }

  const handlePropertySaved = () => {
    // Invalidate the main properties cache so home page shows updated data
    sessionStorage.removeItem('propertiesCache')

    // Refresh properties list after adding/editing
    fetchMyProperties(1, false, statusFilter, typeFilter, debouncedSearchTerm)
  }

  const handleLoadMore = async () => {
    if (loadingMore || !pagination.hasMore) return
    const nextPage = pagination.page + 1
    fetchMyProperties(nextPage, true, statusFilter, typeFilter, debouncedSearchTerm)
  }

  const resolveStatusFilterValue = (statusLabel) => {
    const normalizedLabel = String(statusLabel || '').trim().toLowerCase()
    if (!normalizedLabel) return ''

    const matchingOption = statusOptions.find((option) => {
      const optionValue = String(option?.value || '').trim().toLowerCase()
      const optionLabel = String(option?.label || '').trim().toLowerCase()
      return optionValue === normalizedLabel || optionLabel === normalizedLabel
    })

    return matchingOption?.value || String(statusLabel).trim()
  }

  const handleStatusSummaryClick = (statusLabel) => {
    const nextStatusValue = resolveStatusFilterValue(statusLabel)
    setStatusFilter((prevStatus) => {
      const normalizedPrev = String(prevStatus || '').trim().toLowerCase()
      const normalizedNext = String(nextStatusValue || '').trim().toLowerCase()

      // Clicking the same status again clears the filter.
      if (normalizedPrev && normalizedPrev === normalizedNext) {
        return ''
      }

      return nextStatusValue
    })
  }

  const isStatusSummaryActive = (statusLabel) => {
    const selectedStatus = String(statusFilter || '').trim().toLowerCase()
    if (!selectedStatus) return false

    const resolvedStatus = String(resolveStatusFilterValue(statusLabel) || '').trim().toLowerCase()
    return resolvedStatus === selectedStatus
  }

  const getStatusClassName = (status) => {
    const normalized = String(status || '').trim().toLowerCase()

    if (normalized === 'listed') return 'status-available'
    if (normalized === 'pending verification') return 'status-under-verification'
    if (normalized === 'unlisted/inactive') return 'status-inactive'
    if (normalized === 'archived') return 'status-default'
    if (normalized === 'available') return 'status-available'
    if (normalized === 'inactive') return 'status-inactive'
    if (normalized === 'sold by others') return 'status-sold-others'
    if (normalized === 'incomplete information') return 'status-incomplete-information'
    if (normalized === 'not responding') return 'status-not-responding'
    if (normalized === 'sold by vruddhi') return 'status-sold-vruddhi'
    if (normalized === 'additional options') return 'status-additional-options'
    if (normalized === 'draft') return 'status-draft'

    return 'status-default'
  }

  const statusCountsForDisplay = useMemo(() => {
    if (!Array.isArray(individualStatusCounts)) return []
    const draftStatuses = individualStatusCounts.filter(
      (item) => String(item?.status || '').trim().toLowerCase() === 'draft',
    )
    const nonDraftStatuses = individualStatusCounts.filter(
      (item) => String(item?.status || '').trim().toLowerCase() !== 'draft',
    )
    return [...nonDraftStatuses, ...draftStatuses]
  }, [individualStatusCounts])

  const totalCountFromStatusCards = useMemo(() => {
    return statusCountsForDisplay.reduce((sum, item) => sum + Number(item?.count || 0), 0)
  }, [statusCountsForDisplay])

  const visibleStatusCounts = useMemo(() => {
    if (!isMobileView) return statusCountsForDisplay
    return statusCountsForDisplay.slice(statusPageStart, statusPageStart + statusPageSize)
  }, [statusCountsForDisplay, statusPageStart, statusPageSize, isMobileView])

  const hasStatusPagination = isMobileView && statusCountsForDisplay.length > statusPageSize

  const handleNextStatusPage = () => {
    if (!hasStatusPagination) return
    const nextStart = statusPageStart + statusPageSize
    setStatusPageStart(nextStart >= statusCountsForDisplay.length ? 0 : nextStart)
  }

  const handlePreviousStatusPage = () => {
    if (!hasStatusPagination) return
    const prevStart = statusPageStart - statusPageSize
    if (prevStart < 0) {
      const remainder = statusCountsForDisplay.length % statusPageSize
      const lastPageStart = remainder === 0
        ? Math.max(statusCountsForDisplay.length - statusPageSize, 0)
        : Math.max(statusCountsForDisplay.length - remainder, 0)
      setStatusPageStart(lastPageStart)
      return
    }
    setStatusPageStart(prevStart)
  }

  // Search and filter are backend-driven to match Home page behavior.
  const filteredProperties = useMemo(() => properties, [properties])

  if (loading) {
    return (
      <MainContentWrapper hideHeaderOnDesktop>
        <Container sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </MainContentWrapper>
    )
  }

  return (
    <MainContentWrapper hideHeaderOnDesktop>
      <Container
        className='my-listings-page'
        maxWidth={false}
        disableGutters
        sx={{
          py: 2,
          px: { xs: 2, sm: 2, md: '40px', lg: '80px' },
          overflowX: 'hidden',
        }}
      >
          {!isMobileView && (
            <header className='page-header'>
              <div className='header-content'>
                <h1 className='page-title'>My Listing</h1>
                <p className='page-subtitle'>Manage your property listings, review details, track status, and keep your property information up to date. New property submissions are temporarily unavailable and will be enabled soon.</p>
              </div>
              <div className='header-actions'>
                <button className='add-property' onClick={handleListPropertyClick}>+ List Property</button>
              </div>
            </header>
          )}

          {/* Summary */}
          <div className={`summary-row summary-status-row ${isMobileView ? 'summary-status-row-mobile' : 'summary-status-row-desktop'}`}>
            <div
              className={`summary-card total ${!statusFilter ? 'summary-card-active' : ''}`}
              role='button'
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => setStatusFilter('')}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  setStatusFilter('')
                }
              }}
              aria-label='Show all status listings'
            >
              <div className='summary-icon'><HomeIcon /></div>
              <div className='summary-info'>
                <div className='summary-label'>Total</div>
                <div className='summary-count'>
                  {statusCountsForDisplay.length > 0 ? totalCountFromStatusCards : counts.total}
                </div>
              </div>
            </div>
            {hasStatusPagination && !isMobileView && (
              <button
                className='summary-inline-arrow'
                type='button'
                aria-label='Show previous status counts'
                onClick={handlePreviousStatusPage}
              >
                <ChevronLeftIcon fontSize='small' />
              </button>
            )}
            {(isMobileView ? visibleStatusCounts.slice(0, 1) : visibleStatusCounts).map((item) => (
                  <div
                    className={`summary-card ${getStatusClassName(item.status)} ${isStatusSummaryActive(item.status) ? 'summary-card-active' : ''}`}
                    key={String(item.status)}
                    role='button'
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleStatusSummaryClick(item.status)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        handleStatusSummaryClick(item.status)
                      }
                    }}
                    aria-label={`Filter listings by ${item.status}`}
                  >
                    <div className='summary-icon'><HomeIcon /></div>
                    <div className='summary-info'>
                      <div className='summary-label'>{item.status}</div>
                      <div className='summary-count'>{item.count || 0}</div>
                    </div>
                  </div>
                ))}
            {hasStatusPagination && (
              <button
                className='summary-inline-arrow'
                type='button'
                aria-label='Show next status counts'
                onClick={handleNextStatusPage}
              >
                <ChevronRightIcon fontSize='small' />
              </button>
            )}
          </div>

          {/* Controls */}
          <section className='controls-row'>
            <div className='search-wrap'>
              <input
                className='search-input'
                placeholder="Search properties by name, ID, location or contact"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            <div className='filters-and-toggles'>
              <div className='filter-dropdowns'>
                <select
                  className='filter-dropdown'
                  value={typeFilter}
                  onChange={(event) => setTypeFilter(event.target.value)}
                >
                  <option value="">All Types</option>
                  {propertyTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
                <select
                  className='filter-dropdown'
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="">All Status</option>
                  {statusOptions.map((status) => (
                    <option key={String(status.id ?? status.value)} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>
              <ViewModeSelector
                currentView={viewMode}
                onViewChange={setViewMode}
                showMap={true}
              />
            </div>
          </section>

          <div className='results-info'>
            Showing {filteredProperties.length} of {pagination.total || properties.length} properties
          </div>

          {filteredProperties.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <h6 style={{ color: '#666', marginBottom: '16px' }}>
                You haven't listed any properties yet.
                 New property listings are temporarily unavailable and will be enabled soon.
              </h6>
              <p style={{ color: '#999' }}>
                {/* Click "Add New Property" button above to add your first property */}
              </p>
            </Box>
          ) : viewMode === 'list' ? (
            <PropertyListView
              properties={filteredProperties}
              onViewDetails={handleViewDetails}
              isLoggedIn={isLoggedIn}
              user={user}
              showEditDelete={true}
              onEdit={handleEditProperty}
              onDelete={(prop) => handleDeleteProperty(prop)}
              onUnlist={(prop) => handleUnlistProperty(prop)}
            />
          ) : viewMode === 'map' ? (
            <Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}>
              <PropertyMapView
                properties={filteredProperties}
                onViewDetails={handleViewDetails}
                isLoggedIn={isLoggedIn}
                user={user}
                renderPropertyCardPopup={true}
                showEditDelete={true}
                onEdit={handleEditProperty}
                onDelete={(prop) => handleDeleteProperty(prop)}
                onUnlist={(prop) => handleUnlistProperty(prop)}
                disableContactButton={true}
              />
            </Suspense>
          ) : (
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(4, 1fr)',
                xl: 'repeat(5, 1fr)',
                xxl: 'repeat(6, 1fr)'
              },
              gap: 3,
              maxWidth: '100%',
              overflow: 'hidden',
              '& > *': {
                minHeight: 0,
                minWidth: 0
              }
            }}>
              {filteredProperties.map((property) => (
                <PropertyCard
                  key={property.property_id || property.id}
                  property={property}
                  onViewDetails={handleViewDetails}
                  isLoggedIn={isLoggedIn}
                  user={user}
                  showEditDelete={true}
                  onEdit={handleEditProperty}
                  onDelete={(prop) => handleDeleteProperty(prop)}
                  onUnlist={(prop) => handleUnlistProperty(prop)}
                  disableContactButton={true}
                />
              ))}
            </Box>
          )}

          {pagination.hasMore && viewMode !== 'map' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
              <Button
                variant='contained'
                size='large'
                onClick={handleLoadMore}
                disabled={loadingMore}
                sx={{
                  px: 6,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                {loadingMore ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                    Loading...
                  </>
                ) : (
                  'View More Properties'
                )}
              </Button>
            </Box>
          )}
        </Container>

        <Suspense fallback={null}>
          <PropertyListingForm
            isOpen={isListingFormOpen}
            onClose={handleCloseListingForm}
            editProperty={editingProperty}
            onSuccess={handlePropertySaved}
            saveMode='property-progress'
            createSuccessMessage='Property sent for verification. It will be displayed on the portal once approved.'
            prefillOwnerBrokerFromUser
          />
        </Suspense>
      </MainContentWrapper>
  )
}
